import { PhoneNumber, Store, StoreUserRoleMapping } from '@/interfaces';
import { setCurrentUser, setStoreList } from '@/features/auth';
import { AsyncStorageKeyEnum, baseSplitApi } from '@/services';
import { BuyerTypeEnum, StorePermissionEnum, StoreRoleEnum, StoreTypeEnum } from '@/enum';
import { RootState } from '@/store';
import {
  StoreType,
  StoreTypeListResponse,
  StoreCategoryItem,
  RawStoreTypeResponse,
  RawStoreTypePayload,
} from '../types/storeType';
// import StorageService from '../../db';

type StoreUserPermissionPayload = {
  // Only the granted permissions, sent as an array.
  permissionOverrides?: StorePermissionEnum[];
};

type CreateStoreUserPayload = PhoneNumber & {
  name: string;
  storeRole: StoreRoleEnum;
  title: string;
} & StoreUserPermissionPayload;

type UpdateStoreUserPayload = {
  userId: string;
  name?: string;
  storeRole?: StoreRoleEnum;
  title?: string;
} & StoreUserPermissionPayload;

export const storeApi = baseSplitApi.injectEndpoints({
  endpoints: build => ({
    storeSelf: build.query<Store, any>({
      query: () => ({
        url: '/v1/store/self?profile-detail=true',
        method: 'GET',
      }),
      providesTags: ['StoreProfile'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled, updateCachedData }) {
          const response = await queryFulfilled;
          dispatch(setCurrentUser({ ...response.data, profileType: BuyerTypeEnum.Store }));
          window.localStorage.setItem(
            AsyncStorageKeyEnum.CurrentUser,
            JSON.stringify({...response.data, profileType: BuyerTypeEnum.Store }),
          );
      },
      transformResponse: (response: { data: Store }) => response.data,
    }),
    storeList: build.query<Store[], URLSearchParams | string>({
      query: args => ({
        url: '/v1/store/my-store' ,//+ args,
        method: 'GET',
      }),
      transformResponse: (response: { data: Store[] }) => response.data,
    }),
    createStore: build.mutation<Store, Partial<Store>>({
      query: (body: Partial<Store>) => ({
        url: `/v1/store`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['StoreProfile', 'StoreList'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        const response = await queryFulfilled;
        if (response?.data) {
          dispatch(
            setCurrentUser({
              ...response.data,
              profileType: BuyerTypeEnum.Store,
            }),
          );
          window.localStorage.setItem(
            AsyncStorageKeyEnum.CurrentUser,
            JSON.stringify({
              ...response.data,
              profileType: BuyerTypeEnum.Store,
            }),
          );
        }
      },
      transformResponse: (response: { data: Store }) => response.data,
    }),
    updateStore: build.mutation({
      query: (patch) => {
        return {
          url: `/v1/store`,
          method: 'PATCH',
          body: patch,
        };
      },
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        const response = await queryFulfilled;
        dispatch(setCurrentUser({ ...response?.data, profileType: BuyerTypeEnum.Store }));
        await window.localStorage.setItem(
          AsyncStorageKeyEnum.CurrentUser,
          JSON.stringify({...response.data, profileType: BuyerTypeEnum.Store }),
        );
      },
      transformResponse: (response: { data: Store }) => response.data,
    }),
    deleteStore: build.mutation({
      query: (patch) => {
        return {
          url: `/v1/store`,
          method: 'DELETE',
          body: {},
        };
      },
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        const response = await queryFulfilled;
        if (response.data) {
          dispatch(setCurrentUser(null));
          await localStorage.removeItem(AsyncStorageKeyEnum.CurrentUser);
        }
      },
      transformResponse: (response: { data: Store }) => response.data,
    }),
    createStoreProfile: build.mutation<Store, Partial<Store>>({
      query: (body: Partial<Store>) => ({
        url: `/v1/store/profile`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['StoreProfile'],
      transformResponse: (response: { data: Store }) => response.data,
    }),
    updateStoreProfile: build.mutation<Store, Partial<Store>>({
      query: (body: Partial<Store>) => ({
        url: `/v1/store/profile`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['StoreProfile'],
      transformResponse: (response: { data: Store }) => response.data,
    }),

    storeTypeList: build.query<
      StoreTypeListResponse,
      Record<string, string | number | boolean> | void
    >({
      query: (params) => ({
        url: '/v1/store/type',
        method: 'GET',
        params: { grouped: true, ...(params || {}) },
      }),
      keepUnusedDataFor: 0,
      transformResponse: (
        response: RawStoreTypeResponse,
      ): StoreTypeListResponse => {
        try {
          const raw: RawStoreTypePayload = response?.data ?? response;

          let popular: StoreType[] = [];
          let storeTypes: StoreType[] = [];
          const categories: Record<string, { label: string }> = {};
          const grouped: Record<string, StoreType[]> = {};

          if (
            !Array.isArray(raw) &&
            Array.isArray(raw.categorisedStoreTypeList)
          ) {
            raw.categorisedStoreTypeList.forEach(cat => {
              const catLabel =
                cat?.viewValue?.en || cat?.categoryName || 'Unknown';
              const catId = cat?.value || cat?.key || cat?._id || catLabel;

              categories[catId] = { label: catLabel };

              if (Array.isArray(cat.storeTypeList)) {
                const items: StoreType[] = cat.storeTypeList.map(item => ({
                  label: item?.viewValue?.en || item?.label || item?.value || '',
                  value: item?.value || '',
                  url:
                    typeof item?.url === 'string'
                      ? item.url
                      : item?.url?.coloured || '',
                  categoryId: catId,
                  viewValue: {
                    en: item?.viewValue?.en || item?.label || item?.value || '',
                  },
                }));

                grouped[catId] = items;
                storeTypes.push(...items);
              }
            });

            if (Array.isArray(raw.popularStoreTypeList)) {
              popular = raw.popularStoreTypeList
                .map(key => storeTypes.find(s => s.value === key))
                .filter((item): item is StoreType => !!item);
            }
          } else if (Array.isArray(raw)) {
            storeTypes = raw.map(item => ({
              label: item?.viewValue?.en || item?.label || item?.value || '',
              value: item?.value || item?.key || '',
              url:
                typeof item?.url === 'string'
                  ? item.url
                  : item?.url?.coloured || '',
              categoryId: item?.categoryId || item?.value || item?.key || '',
            }));

            const fallbackPopularKeys = [
              'GroceryAndGeneralStore',
              'Pharmacy',
              'ClothingAndApparel',
              'ElectronicAppliances',
              'Bakery',
              'SupermarketAndHypermarket',
              'Jewellery',
            ];

            popular = fallbackPopularKeys
              .map(key => storeTypes.find(s => s.value === key))
              .filter((item: StoreType | undefined): item is StoreType => !!item);
          }

          return { popular, storeTypes, categories, grouped };
        } catch {
          return { popular: [], storeTypes: [], grouped: {}, categories: {} };
        }
      },
    }),
    storeCategoryList: build.query<
      StoreCategoryItem[],
      { page?: number } | void
    >({
      query: () => ({
        url: '/v1/store/category',
        method: 'GET',
      }),
      transformResponse: (response: { data: StoreCategoryItem[] }) =>
        response.data,
    }),
    getStoreList: build.query<Store[], URLSearchParams | string>({
      query: args => ({
        url: '/v1/store?' + args,
        method: 'GET',
      }),
      transformResponse: (response: { data: Store[] }) => response.data,
    }),
    getStore: build.query<
      Store,
      { searchParams: URLSearchParams | string; store: string }
    >({
      query: ({ store, searchParams }) => ({
        url: `/v1/store/${store}?${searchParams ? searchParams : ''}`,
        method: 'GET',
      }),
      transformResponse: (response: { data: Store }) => response.data,
    }),
    getStoreUserList: build.query<StoreUserRoleMapping[], unknown>({
      query: () => ({
        url: `/v1/store/user`,
        method: 'GET',
      }),
      transformResponse: (response: { data: StoreUserRoleMapping[] }) =>
        response.data,
    }),
    createStoreUser: build.mutation<StoreUserRoleMapping, CreateStoreUserPayload>({
      query: (body) => ({
        url: `/v1/store/user`,
        method: 'POST',
        body,
      }),
      transformResponse: (response: { data: StoreUserRoleMapping }) =>
        response.data,
    }),
    updateStoreUser: build.mutation<StoreUserRoleMapping, UpdateStoreUserPayload>({
      query: ({ userId, ...body }) => ({
        url: `/v1/store/user/${userId}`,
        method: 'PATCH',
        body,
      }),
      transformResponse: (response: { data: StoreUserRoleMapping }) =>
        response.data,
    }),
    deleteStoreUser: build.mutation<any, string>({
      query: userId => ({
        url: `/v1/store/user/${userId}`,
        method: 'DELETE',
        body: {},
      }),
      transformResponse: (response: { data: any }) => response.data,
    }),
  }),
  overrideExisting: false,
});

// const userSelector = useSelector((state:RootState)=)
export const {
  useStoreListQuery,
  useStoreSelfQuery,
  useCreateStoreMutation,
  useUpdateStoreMutation,
  useLazyStoreSelfQuery,
  useLazyStoreListQuery,
  useGetStoreListQuery,
  useGetStoreQuery,
  useLazyGetStoreListQuery,
  useLazyGetStoreQuery,
  useLazyStoreTypeListQuery,
  useStoreTypeListQuery,
  useStoreCategoryListQuery,
  useUpdateStoreProfileMutation,
  useCreateStoreProfileMutation,
  useDeleteStoreUserMutation,
  useUpdateStoreUserMutation,
  useCreateStoreUserMutation,
  useGetStoreUserListQuery,
  useLazyGetStoreUserListQuery,
  useDeleteStoreMutation
} = storeApi;

export const storeListSelector = (state: RootState) => state.auth.storeList;
export const currentUserSelector = (state: RootState) => state.auth.currentUser;
